/**
 * User Template Service
 * 
 * Manages user-created email templates in the user_email_templates collection.
 * 
 * Purpose:
 * - User's personal templates (workspace-scoped)
 * - Can be created from scratch or copied from system templates
 * - Private to the user and workspace
 * 
 * Collection: user_email_templates
 * Scoping: userId + workspaceId
 */

import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  doc
} from 'firebase/firestore';
import { db } from '../firebase';
import { systemTemplateService, SystemTemplate } from './systemTemplateService';

// ============================================================================
// Types & Interfaces
// ============================================================================

export type UserTemplateCategory =
  | 'arts-entertainment'
  | 'blogs-updates'
  | 'business'
  | 'deals-offers'
  | 'ecommerce'
  | 'events'
  | 'festive-season'
  | 'health-wellness'
  | 'newsletter'
  | 'onboarding'
  | 'professional-services'
  | 'promotional';

export interface UserEmailTemplate {
  id?: string;
  name: string;
  subject: string;
  htmlContent: string;
  textContent?: string;
  category: UserTemplateCategory;
  tags: string[];
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  
  // Ownership (NEW: workspace-scoped)
  createdBy: string; // User ID who created/edited this template
  workspaceId: string; // Workspace this template belongs to
  
  // Template origin (NEW: track if copied from system template)
  sourceTemplateId?: string; // ID from email_templates collection
  isFromSystemTemplate?: boolean; // true if copied from system template
  
  // Content
  variables: string[]; // List of merge tags used in the template
  previewImage?: string; // URL to preview image
  description?: string;
  
  // Editor design JSON for drag-and-drop builder
  designJson?: unknown;
  
  // Legacy fields (for backward compatibility)
  originalAdminTemplateId?: string; // Old field name
  isFromPremiumTemplate?: boolean; // Old field name
}

export interface UserTemplateCreateData {
  name: string;
  subject: string;
  htmlContent: string;
  textContent?: string;
  category: UserTemplateCategory;
  tags: string[];
  variables: string[];
  description?: string;
  designJson?: unknown;
  previewImage?: string;
  
  // Template origin
  sourceTemplateId?: string;
  isFromSystemTemplate?: boolean;
  
  // Legacy support
  originalAdminTemplateId?: string;
  isFromPremiumTemplate?: boolean;
}

// ============================================================================
// User Template Service Class
// ============================================================================

class UserTemplateService {
  private readonly collectionName = 'user_email_templates';

  /**
   * Get all user templates for a specific workspace
   * 
   * @param userId - User ID
   * @param workspaceId - Workspace ID
   * @returns User's templates in this workspace
   */
  async getUserTemplates(userId: string, workspaceId: string): Promise<UserEmailTemplate[]> {
    try {
      // Try with ordering first
      try {
        const q = query(
          collection(db, this.collectionName),
          where('createdBy', '==', userId),
          where('workspaceId', '==', workspaceId),
          orderBy('updatedAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as UserEmailTemplate[];
      } catch (indexError: any) {
        // If index doesn't exist yet, fall back to query without orderBy
        console.warn('Firestore index not available, using basic query:', indexError.message);
        const q = query(
          collection(db, this.collectionName),
          where('createdBy', '==', userId),
          where('workspaceId', '==', workspaceId)
        );
        const querySnapshot = await getDocs(q);
        const templates = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as UserEmailTemplate[];

        // Sort in memory by updatedAt
        return templates.sort((a, b) => {
          const aTime = a.updatedAt?.toMillis?.() || 0;
          const bTime = b.updatedAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
      }
    } catch (error) {
      console.error('Error getting user templates:', error);
      throw new Error('Failed to get user templates');
    }
  }

  /**
   * Get a single user template by ID (with ownership verification)
   * 
   * @param templateId - Template document ID
   * @param userId - User ID (for ownership check)
   * @param workspaceId - Optional workspace ID (for additional verification)
   * @returns Template or null if not found/not owned
   */
  async getTemplateById(
    templateId: string,
    userId: string,
    workspaceId?: string
  ): Promise<UserEmailTemplate | null> {
    try {
      const templateRef = doc(db, this.collectionName, templateId);
      const templateSnap = await getDoc(templateRef);

      if (!templateSnap.exists()) {
        return null;
      }

      const template = {
        id: templateSnap.id,
        ...templateSnap.data(),
      } as UserEmailTemplate;

      // Verify ownership
      if (template.createdBy !== userId) {
        console.warn(`Template ${templateId} does not belong to user ${userId}`);
        return null;
      }

      // Optionally verify workspace
      if (workspaceId && template.workspaceId !== workspaceId) {
        console.warn(`Template ${templateId} does not belong to workspace ${workspaceId}`);
        return null;
      }

      return template;
    } catch (error) {
      console.error('Error getting user template by ID:', error);
      throw new Error('Failed to get user template');
    }
  }

  /**
   * Create a new user template from scratch
   * 
   * @param templateData - Template data
   * @param userId - User ID
   * @param workspaceId - Workspace ID
   * @returns Template ID
   */
  async createTemplate(
    templateData: UserTemplateCreateData,
    userId: string,
    workspaceId: string
  ): Promise<string> {
    try {
      console.log('Creating user template:', {
        name: templateData.name,
        userId,
        workspaceId,
        htmlContentLength: templateData.htmlContent?.length || 0,
        hasDesignJson: !!templateData.designJson,
      });

      // Validate required fields
      if (!templateData.name || templateData.name.trim() === '') {
        throw new Error('Template name is required');
      }
      if (!templateData.htmlContent || templateData.htmlContent.trim() === '') {
        throw new Error('HTML content is required');
      }
      if (!userId || !workspaceId) {
        throw new Error('User ID and Workspace ID are required');
      }

      // Build the base document without designJson first
      const baseDoc = {
        name: templateData.name.trim(),
        subject: templateData.subject?.trim() || '',
        htmlContent: templateData.htmlContent,
        textContent: templateData.textContent || '',
        category: templateData.category,
        tags: Array.isArray(templateData.tags) ? templateData.tags : [],
        variables: Array.isArray(templateData.variables) ? templateData.variables : [],
        description: templateData.description || '',
        previewImage: templateData.previewImage || '',
        
        // Ownership
        createdBy: userId,
        workspaceId: workspaceId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        isActive: true,
        
        // Template origin
        ...(templateData.sourceTemplateId && { sourceTemplateId: templateData.sourceTemplateId }),
        ...(templateData.isFromSystemTemplate !== undefined && {
          isFromSystemTemplate: templateData.isFromSystemTemplate,
        }),
        
        // Legacy fields (backward compatibility)
        ...(templateData.originalAdminTemplateId && {
          originalAdminTemplateId: templateData.originalAdminTemplateId,
        }),
        ...(templateData.isFromPremiumTemplate !== undefined && {
          isFromPremiumTemplate: templateData.isFromPremiumTemplate,
        }),
      };

      // Check base document for invalid types
      console.log('Checking base document for invalid types...');
      const baseIssues = this.findInvalidFirestoreTypes(baseDoc, 'baseDoc');
      if (baseIssues.length > 0) {
        console.error('⚠️ Found invalid types in base document:', baseIssues);
        throw new Error('Base document contains invalid data types: ' + baseIssues.join(', '));
      }

      // Handle designJson separately with extra validation
      let finalDesignJson = undefined;
      if (templateData.designJson) {
        try {
          console.log('Processing designJson...');

          // Check for invalid types BEFORE cleaning
          const designJsonIssues = this.findInvalidFirestoreTypes(
            templateData.designJson,
            'designJson'
          );
          if (designJsonIssues.length > 0) {
            console.warn(
              '⚠️ Found invalid types in designJson (will attempt to clean):',
              designJsonIssues.slice(0, 10)
            );
          }

          // First, serialize and deserialize to remove circular references
          const serialized = JSON.stringify(templateData.designJson);
          const deserialized = JSON.parse(serialized);
          // Then clean for Firestore-compatible types
          finalDesignJson = this.cleanForFirestore(deserialized);

          // Validate after cleaning
          const cleanedIssues = this.findInvalidFirestoreTypes(
            finalDesignJson,
            'cleanedDesignJson'
          );
          if (cleanedIssues.length > 0) {
            console.error('❌ Still found invalid types after cleaning:', cleanedIssues);
            console.log('Skipping designJson to allow template save');
            finalDesignJson = undefined;
          }

          // Check size after cleaning
          if (finalDesignJson) {
            const cleanedSize = new Blob([JSON.stringify(finalDesignJson)]).size;
            console.log('Cleaned designJson size:', cleanedSize, 'bytes');

            if (cleanedSize > 800000) {
              console.warn('designJson too large after cleaning, skipping to avoid Firestore limit');
              finalDesignJson = undefined;
            }
          }
        } catch (jsonError: any) {
          console.error('Failed to process designJson:', jsonError.message);
          finalDesignJson = undefined;
        }
      }

      // Build final document
      const templateDoc = finalDesignJson ? { ...baseDoc, designJson: finalDesignJson } : baseDoc;

      console.log('Attempting to create document with fields:', Object.keys(templateDoc));

      // Final validation before save
      const finalIssues = this.findInvalidFirestoreTypes(templateDoc, 'finalDoc');
      if (finalIssues.length > 0) {
        console.error('❌ Final document still has invalid types:', finalIssues);
        throw new Error(
          'Document contains invalid Firestore types after cleaning: ' +
            finalIssues.slice(0, 5).join(', ')
        );
      }

      const docRef = await addDoc(collection(db, this.collectionName), templateDoc);
      console.log('✅ Template created successfully with ID:', docRef.id);
      return docRef.id;
    } catch (error: any) {
      console.error('❌ Error creating user template:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Template data summary:', {
        name: templateData.name,
        category: templateData.category,
        userId: userId,
        workspaceId: workspaceId,
        htmlLength: templateData.htmlContent?.length || 0,
      });

      // Return more specific error messages
      if (error.code === 'permission-denied') {
        throw new Error('Permission denied. Please check your authentication.');
      } else if (error.code === 'invalid-argument') {
        throw new Error('Invalid data format. Check console for details.');
      } else if (error.message?.includes('maximum size')) {
        throw new Error('Template is too large. Try removing some content or images.');
      }

      throw new Error(`${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Create a user template by copying a system template
   * 
   * @param systemTemplateId - System template ID from email_templates collection
   * @param userId - User ID
   * @param workspaceId - Workspace ID
   * @param customizations - Optional name/subject/content overrides
   * @returns New user template ID
   */
  async createFromSystemTemplate(
    systemTemplateId: string,
    userId: string,
    workspaceId: string,
    customizations?: {
      name?: string;
      subject?: string;
      htmlContent?: string;
      description?: string;
    }
  ): Promise<string> {
    try {
      console.log(`Creating user template from system template: ${systemTemplateId}`);

      // Fetch the system template
      const systemTemplate = await systemTemplateService.getTemplateById(systemTemplateId);
      if (!systemTemplate) {
        throw new Error(`System template not found: ${systemTemplateId}`);
      }

      // Track download count in system template
      await systemTemplateService.incrementDownloadCount(systemTemplateId);

      // Create user template with system template data
      const templateData: UserTemplateCreateData = {
        name: customizations?.name || `${systemTemplate.name} (Copy)`,
        subject: customizations?.subject || systemTemplate.subject,
        htmlContent: customizations?.htmlContent || systemTemplate.htmlContent,
        textContent: systemTemplate.textContent,
        category: systemTemplate.category as UserTemplateCategory,
        tags: [...systemTemplate.tags, 'from-system-template'],
        variables: systemTemplate.variables,
        description:
          customizations?.description ||
          `Copied from system template: ${systemTemplate.name}`,
        previewImage: systemTemplate.previewImage,
        designJson: systemTemplate.designJson,
        
        // Track origin
        sourceTemplateId: systemTemplateId,
        isFromSystemTemplate: true,
      };

      const templateId = await this.createTemplate(templateData, userId, workspaceId);

      console.log(
        `✅ Created user template ${templateId} from system template ${systemTemplateId}`
      );
      return templateId;
    } catch (error) {
      console.error('Error creating user template from system template:', error);
      throw new Error('Failed to create template from system template');
    }
  }

  /**
   * Legacy method: Create user template from admin template
   * (For backward compatibility)
   * 
   * @deprecated Use createFromSystemTemplate instead
   */
  async createFromAdminTemplate(
    adminTemplate: any,
    userId: string,
    workspaceId: string,
    customizations?: {
      name?: string;
      subject?: string;
      htmlContent?: string;
    }
  ): Promise<string> {
    try {
      console.warn('createFromAdminTemplate is deprecated. Use createFromSystemTemplate instead.');

      const templateData: UserTemplateCreateData = {
        name: customizations?.name || `${adminTemplate.name} (Copy)`,
        subject: customizations?.subject || adminTemplate.subject || '',
        htmlContent: customizations?.htmlContent || adminTemplate.htmlContent,
        category: 'arts-entertainment',
        tags: [...(adminTemplate.tags || []), 'user-created'],
        variables: adminTemplate.variables || ['first_name', 'last_name', 'email'],
        description: `Created from admin template: ${adminTemplate.name}`,
        originalAdminTemplateId: adminTemplate.id,
        isFromPremiumTemplate: adminTemplate.isPaid || false,
      };

      return await this.createTemplate(templateData, userId, workspaceId);
    } catch (error) {
      console.error('Error creating user template from admin template:', error);
      throw new Error('Failed to create user template from admin template');
    }
  }

  // Helper function to detect invalid Firestore types
  private findInvalidFirestoreTypes(obj: any, path: string = 'root'): string[] {
    const issues: string[] = [];
    
    if (obj === null) return issues;
    if (obj === undefined) {
      issues.push(`${path}: undefined`);
      return issues;
    }
    
    const objType = typeof obj;
    
    if (objType === 'function') {
      issues.push(`${path}: function`);
      return issues;
    }
    
    if (objType === 'symbol') {
      issues.push(`${path}: symbol`);
      return issues;
    }
    
    if (obj instanceof Date || obj instanceof Timestamp) {
      return issues;
    }
    
    // Check for DOM elements
    if (typeof obj === 'object' && obj !== null && 'nodeType' in obj) {
      issues.push(`${path}: DOM element`);
      return issues;
    }
    
    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        issues.push(...this.findInvalidFirestoreTypes(item, `${path}[${index}]`));
      });
      return issues;
    }
    
    if (objType === 'object') {
      // Check for circular references by attempting to stringify
      try {
        JSON.stringify(obj);
      } catch (e) {
        issues.push(`${path}: circular reference or non-serializable`);
        return issues;
      }
      
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          issues.push(...this.findInvalidFirestoreTypes(obj[key], `${path}.${key}`));
        }
      }
    }
    
    return issues;
  }

  // Helper function to clean data for Firestore (remove undefined, functions, etc.)
  private cleanForFirestore(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    if (typeof obj === 'function' || typeof obj === 'symbol') {
      return null;
    }
    
    if (obj instanceof Date || obj instanceof Timestamp) {
      return obj;
    }
    
    // Check for DOM elements
    if (typeof obj === 'object' && obj !== null && 'nodeType' in obj) {
      return null;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.cleanForFirestore(item)).filter(item => item !== null);
    }
    
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const cleanedValue = this.cleanForFirestore(obj[key]);
          if (cleanedValue !== null && cleanedValue !== undefined) {
            cleaned[key] = cleanedValue;
          }
        }
      }
      return cleaned;
    }
    
    return obj;
  }

  /**
   * Update user template (with ownership verification)
   * 
   * @param templateId - Template document ID
   * @param updates - Fields to update
   * @param userId - User ID (for ownership check)
   * @param workspaceId - Optional workspace ID (for verification)
   */
  async updateTemplate(
    templateId: string,
    updates: Partial<UserEmailTemplate>,
    userId: string,
    workspaceId?: string
  ): Promise<void> {
    try {
      console.log('Updating template:', templateId, 'with keys:', Object.keys(updates));

      // Verify ownership
      const existing = await this.getTemplateById(templateId, userId, workspaceId);
      if (!existing) {
        throw new Error('Template not found or you do not have permission to update it');
      }

      // Check base updates for invalid types
      const baseUpdates = { ...updates };
      delete baseUpdates.designJson; // Handle separately

      const baseIssues = this.findInvalidFirestoreTypes(baseUpdates, 'updates');
      if (baseIssues.length > 0) {
        console.error('⚠️ Found invalid types in update data:', baseIssues);
      }

      // Clean designJson if it exists in updates
      let cleanedUpdates = { ...updates };
      if (updates.designJson) {
        try {
          console.log('Processing designJson for update...');

          // Check for invalid types before cleaning
          const designJsonIssues = this.findInvalidFirestoreTypes(updates.designJson, 'designJson');
          if (designJsonIssues.length > 0) {
            console.warn(
              '⚠️ Found invalid types in designJson (will attempt to clean):',
              designJsonIssues.slice(0, 10)
            );
          }

          const serialized = JSON.stringify(updates.designJson);
          const deserialized = JSON.parse(serialized);
          cleanedUpdates.designJson = this.cleanForFirestore(deserialized);

          // Validate after cleaning
          const cleanedIssues = this.findInvalidFirestoreTypes(
            cleanedUpdates.designJson,
            'cleanedDesignJson'
          );
          if (cleanedIssues.length > 0) {
            console.error('❌ Still found invalid types after cleaning:', cleanedIssues);
            console.log('Skipping designJson in update');
            delete cleanedUpdates.designJson;
          }

          if (cleanedUpdates.designJson) {
            const cleanedSize = new Blob([JSON.stringify(cleanedUpdates.designJson)]).size;
            console.log('Cleaned designJson size for update:', cleanedSize, 'bytes');

            if (cleanedSize > 800000) {
              console.warn('designJson too large for update, skipping');
              delete cleanedUpdates.designJson;
            }
          }
        } catch (jsonError: any) {
          console.error('Failed to serialize designJson for update:', jsonError.message);
          delete cleanedUpdates.designJson;
        }
      }

      // Final validation
      const finalIssues = this.findInvalidFirestoreTypes(cleanedUpdates, 'finalUpdates');
      if (finalIssues.length > 0) {
        console.error('❌ Update data still has invalid types:', finalIssues);
        throw new Error(
          'Update contains invalid Firestore types: ' + finalIssues.slice(0, 5).join(', ')
        );
      }

      const templateRef = doc(db, this.collectionName, templateId);
      await updateDoc(templateRef, {
        ...cleanedUpdates,
        updatedAt: Timestamp.now(),
      });
      console.log('✅ Template updated successfully');
    } catch (error: any) {
      console.error('❌ Error updating user template:', error);
      console.error('Template ID:', templateId);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);

      if (error.code === 'invalid-argument') {
        throw new Error('Invalid data format. Check console for details.');
      }

      throw new Error(`${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Delete user template (with ownership verification)
   * 
   * @param templateId - Template document ID
   * @param userId - User ID (for ownership check)
   * @param workspaceId - Optional workspace ID (for verification)
   */
  async deleteTemplate(
    templateId: string,
    userId: string,
    workspaceId?: string
  ): Promise<void> {
    try {
      // Verify ownership
      const existing = await this.getTemplateById(templateId, userId, workspaceId);
      if (!existing) {
        throw new Error('Template not found or you do not have permission to delete it');
      }

      await deleteDoc(doc(db, this.collectionName, templateId));
      console.log(`✅ Template deleted: ${templateId}`);
    } catch (error) {
      console.error('Error deleting user template:', error);
      throw new Error('Failed to delete user template');
    }
  }

  /**
   * Get all user templates across all workspaces (admin only)
   * 
   * @returns All user templates
   */
  async getAllUserTemplates(): Promise<UserEmailTemplate[]> {
    try {
      const q = query(collection(db, this.collectionName), orderBy('updatedAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as UserEmailTemplate[];
    } catch (error) {
      console.error('Error getting all user templates:', error);
      throw new Error('Failed to get all user templates');
    }
  }

  /**
   * Search user templates within a workspace
   * 
   * @param userId - User ID
   * @param workspaceId - Workspace ID
   * @param searchQuery - Search string
   * @returns Matching templates
   */
  async searchTemplates(
    userId: string,
    workspaceId: string,
    searchQuery: string
  ): Promise<UserEmailTemplate[]> {
    try {
      const allTemplates = await this.getUserTemplates(userId, workspaceId);
      const searchLower = searchQuery.toLowerCase();

      return allTemplates.filter(
        (t) =>
          t.name.toLowerCase().includes(searchLower) ||
          t.description?.toLowerCase().includes(searchLower) ||
          t.tags.some((tag) => tag.toLowerCase().includes(searchLower))
      );
    } catch (error) {
      console.error('Error searching user templates:', error);
      throw new Error('Failed to search templates');
    }
  }

  /**
   * Get templates created from a specific system template
   * 
   * @param userId - User ID
   * @param workspaceId - Workspace ID
   * @param systemTemplateId - Source system template ID
   * @returns Templates created from this system template
   */
  async getTemplatesFromSystemTemplate(
    userId: string,
    workspaceId: string,
    systemTemplateId: string
  ): Promise<UserEmailTemplate[]> {
    try {
      const allTemplates = await this.getUserTemplates(userId, workspaceId);
      return allTemplates.filter((t) => t.sourceTemplateId === systemTemplateId);
    } catch (error) {
      console.error('Error getting templates from system template:', error);
      throw new Error('Failed to get templates from system template');
    }
  }
}

export const userTemplateService = new UserTemplateService();
